/**
 * E911 (Phase 5, ADR 0021/0027/0041). Per-device posture with the
 * cross-check blockers spelled out, location assignment (the location
 * anchors the home node and the callback DID the device presents), the 933
 * workflow — a location is verified by an ok on both the primary and backup
 * paths; a device by its location being verified plus an attestation that it
 * is there (ADR 0047)
 * since the last E911-relevant change — plus the node-side sync (push the
 * location model to the nodes; scan for drift) and the emergency-call log.
 * `e911.manage` is a protected permission (§2.5): every mutation here
 * demands a typed confirmation.
 */

import { Fragment, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  assignE911Location,
  getE911Detail,
  getE911Devices,
  getE911Options,
  getE911Sync,
  getEmergencyCallEvents,
  pushE911Sync,
  attestE911Device,
  recordE911Test,
} from "@pbxmgr/api";
import type { E911Status } from "@pbxmgr/api";
import { Alert, Badge, Button, Spinner, TextField } from "@pbxmgr/ui";

import { usePermission } from "../auth/usePermission";
import { Panel } from "../dashboard/panels";
import { summarizePush } from "./e911Sync";

function errorText(err: unknown): string {
  return err instanceof ApiError
    ? (err.detail ?? err.message)
    : "request failed";
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

/** Typed confirmation input (§2.5) — the action enables only on a MAC match. */
function ConfirmMac({
  mac,
  value,
  onChange,
}: {
  mac: string;
  value: string;
  onChange: (v: string) => void;
}): ReactNode {
  return (
    <TextField
      id={`confirm-${mac}`}
      label={`Type the device MAC (${mac}) to confirm — E911 changes are protected`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function DeviceDetail({ status }: { status: E911Status }): ReactNode {
  const canManage = usePermission("e911.manage");
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ["e911", "detail", status.device_id],
    queryFn: () => getE911Detail(status.device_id),
  });
  const options = useQuery({
    queryKey: ["e911", "options"],
    queryFn: getE911Options,
  });

  const [confirm, setConfirm] = useState("");
  const [locationId, setLocationId] = useState(
    String(status.location_id ?? ""),
  );
  const [path, setPath] = useState("primary");
  const [result, setResult] = useState("ok");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const confirmed = confirm.trim().toLowerCase() === status.mac;
  const invalidate = (): void => {
    setError(null);
    void qc.invalidateQueries({ queryKey: ["e911"] });
    void qc.invalidateQueries({ queryKey: ["prov", "devices"] });
  };
  const onError = (err: unknown): void => setError(errorText(err));

  const setLocation = useMutation({
    mutationFn: () =>
      assignE911Location(
        status.device_id,
        locationId ? Number(locationId) : null,
      ),
    onSuccess: invalidate,
    onError,
  });
  const attest = useMutation({
    mutationFn: () => attestE911Device(status.device_id, notes || undefined),
    onSuccess: () => {
      setNotes("");
      invalidate();
    },
    onError,
  });
  const record = useMutation({
    mutationFn: () =>
      recordE911Test(status.device_id, {
        path,
        result,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      setNotes("");
      invalidate();
    },
    onError,
  });

  const submit933 = (event: FormEvent): void => {
    event.preventDefault();
    record.mutate();
  };

  return (
    <Panel title={`E911 — ${status.mac} (${status.label ?? "unlabeled"})`}>
      <p>
        Location: <strong>{status.location_name ?? "none"}</strong> · home node:{" "}
        <strong>{status.home_node_name ?? "none"}</strong> (identity 1) ·
        emergency ANI: <strong>{status.did_number ?? "none"}</strong> (the
        location's callback DID, ADR 0041)
      </p>
      {status.blockers.length > 0 ? (
        <Alert>Verification blocked: {status.blockers.join("; ")}</Alert>
      ) : (
        <p>
          Location {status.location_verified ? "verified" : "not verified"}:
          paths passed since its last change{" "}
          {status.paths_passed.join(", ") || "none"} — required:{" "}
          {status.paths_required.join(" + ")} (ADR 0021: the failover path is
          the load-bearing case). This phone{" "}
          {status.attested
            ? `attested here by ${status.attested_by ?? "?"} at ${status.attested_at ?? "?"}`
            : "not yet attested here"}{" "}
          (ADR 0047: verified = location verified + phone attested).
        </p>
      )}

      {canManage ? (
        <>
          <ConfirmMac mac={status.mac} value={confirm} onChange={setConfirm} />
          <div className="window-form">
            <div className="ui-field">
              <label htmlFor={`loc-${status.device_id}`}>Location</label>
              <select
                id={`loc-${status.device_id}`}
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
              >
                <option value="">— none —</option>
                {(options.data?.locations ?? []).map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                    {loc.home_node_name
                      ? ` · home ${loc.home_node_name}`
                      : " · no home node"}
                    {loc.callback_number
                      ? ` · ${loc.callback_number}`
                      : " · no callback"}
                    {loc.has_dispatchable_address
                      ? ""
                      : " (no dispatchable address)"}
                  </option>
                ))}
              </select>
            </div>
            <Button
              disabled={!confirmed || setLocation.isPending}
              onClick={() => setLocation.mutate()}
            >
              Set location (resets verification; then push to the nodes)
            </Button>
          </div>

          <h3>Record a 933 test</h3>
          <form className="window-form" onSubmit={submit933}>
            <div className="ui-field">
              <label htmlFor={`path-${status.device_id}`}>Path exercised</label>
              <select
                id={`path-${status.device_id}`}
                value={path}
                onChange={(e) => setPath(e.target.value)}
              >
                <option value="primary">home node</option>
                <option value="backup">fallback (home node unreachable)</option>
                <option value="proxy">emergency gateway (G5)</option>
              </select>
            </div>
            <div className="ui-field">
              <label htmlFor={`result-${status.device_id}`}>Result</label>
              <select
                id={`result-${status.device_id}`}
                value={result}
                onChange={(e) => setResult(e.target.value)}
              >
                <option value="ok">ok — correct address read back</option>
                <option value="fail">fail</option>
              </select>
            </div>
            <TextField
              id={`notes-${status.device_id}`}
              label="Notes (address read back, PSAP remarks)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <Button type="submit" disabled={!confirmed || record.isPending}>
              Record 933 result
            </Button>
          </form>
          <h3>Attest this phone is here</h3>
          <p className="ui-hint">
            For a phone that did not place the 933 call itself: you (or the
            installer) vouch it is at {status.location_name ?? "its location"}.
            Becomes verified at once if the location already is.
          </p>
          <Button
            disabled={!confirmed || attest.isPending || status.location_id === null}
            onClick={() => attest.mutate()}
          >
            Attest location
          </Button>
          {!confirmed ? (
            <p className="ui-hint" role="note">
              The buttons in this panel stay disabled until the device MAC (
              {status.mac}) is typed in the confirmation box above.
            </p>
          ) : null}
        </>
      ) : (
        <p className="ui-empty">
          Read-only — e911.manage required for changes.
        </p>
      )}
      {error ? <Alert>{error}</Alert> : null}

      <h3>Test history</h3>
      {detail.isLoading ? (
        <Spinner />
      ) : (detail.data?.history.length ?? 0) === 0 ? (
        <div className="ui-empty">No 933 tests recorded.</div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Path</th>
              <th>Result</th>
              <th>By</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {detail.data?.history.map((t) => (
              <tr key={t.id}>
                <td>{formatWhen(t.tested_at)}</td>
                <td>{t.path}</td>
                <td>
                  <Badge tone={t.result === "ok" ? "online" : "offline"}>
                    {t.result}
                  </Badge>
                </td>
                <td>{t.actor}</td>
                <td>{t.notes ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

/** Node-side sync: what the nodes hold vs. what the platform intends; push on demand. */
function SyncPanel(): ReactNode {
  const canManage = usePermission("e911.manage");
  const qc = useQueryClient();
  const scan = useQuery({ queryKey: ["e911", "sync"], queryFn: getE911Sync });
  const [report, setReport] = useState<string[] | null>(null);
  const [scanNote, setScanNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const push = useMutation({
    mutationFn: pushE911Sync,
    onSuccess: (r) => {
      setError(null);
      setReport(summarizePush(r));
      void qc.invalidateQueries({ queryKey: ["e911"] });
      void qc.invalidateQueries({ queryKey: ["locations"] });
    },
    onError: (err) => setError(errorText(err)),
  });
  const items = scan.data?.items ?? [];
  return (
    <Panel title="Locations on the nodes (ADR 0041)">
      <p>
        Each device's home node must hold its location row (address, callback,
        webhook) and assign the extension to it with the callback as emergency
        ANI. The scheduled <code>e911_sync</code> check re-reads this every
        interval; drift pages through the health channel.
      </p>
      {scan.isLoading ? (
        <Spinner />
      ) : scan.isError ? (
        <Alert>{errorText(scan.error)}</Alert>
      ) : items.length === 0 ? (
        <div className="ui-empty">No device has a location yet.</div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>Device</th>
              <th>Home node</th>
              <th>Callback</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <Fragment key={i.device_id}>
                <tr
                  className={
                    i.problems.length > 0 ? "ui-table__main-row" : undefined
                  }
                >
                  <td>{i.subject}</td>
                  <td>{i.home_node ?? "—"}</td>
                  <td>{i.callback ?? "—"}</td>
                  <td>
                    <Badge tone={i.ready ? "online" : "offline"}>
                      {i.ready ? "in sync" : "drifted"}
                    </Badge>
                  </td>
                </tr>
                {i.problems.length > 0 ? (
                  <tr className="ui-table__detail-row">
                    <td colSpan={4}>
                      <span className="ui-table__detail-label">Problems</span>
                      {i.problems.join("; ")}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
      {(scan.data?.notes.length ?? 0) > 0 ? (
        <Alert>{scan.data?.notes.join(" · ")}</Alert>
      ) : null}
      <p className="ui-hint">
        {scan.isFetching
          ? "Scanning the nodes…"
          : scan.dataUpdatedAt
            ? `Last scanned ${new Date(scan.dataUpdatedAt).toLocaleTimeString()}`
            : ""}
      </p>
      {canManage ? (
        <div className="pairing-actions">
          <Button
            disabled={push.isPending || scan.isFetching}
            onClick={() => push.mutate()}
          >
            {push.isPending ? "Pushing…" : "Push E911 locations to the nodes"}
          </Button>
          <Button
            variant="secondary"
            disabled={scan.isFetching}
            onClick={() => {
              setScanNote(null);
              void scan.refetch().then((r) => {
                const data = r.data;
                setScanNote(
                  data
                    ? `Scan complete at ${new Date().toLocaleTimeString()}: ` +
                        `${data.items.filter((i) => i.ready).length} in sync, ` +
                        `${data.gaps} gap${data.gaps === 1 ? "" : "s"}.`
                    : "Scan failed.",
                );
              });
            }}
          >
            {scan.isFetching ? "Scanning…" : "Re-scan"}
          </Button>
        </div>
      ) : null}
      {scanNote ? <Alert>{scanNote}</Alert> : null}
      {report ? (
        <ul>
          {report.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </Panel>
  );
}

/** Every observed 911/933 call, judged against the home-node rule. */
function EventsPanel(): ReactNode {
  const events = useQuery({
    queryKey: ["e911", "events"],
    queryFn: getEmergencyCallEvents,
    refetchInterval: 30_000,
  });
  const items = events.data?.items ?? [];
  return (
    <Panel title={`Emergency calls observed (${items.length})`}>
      {events.isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <div className="ui-empty">
          No emergency calls observed. 933 test calls appear here from the CDR
          feed (and the Vodia location webhook when configured).
        </div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Source</th>
              <th>Dialed</th>
              <th>From</th>
              <th>Via</th>
              <th>Egress</th>
              <th>Answered</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <Fragment key={e.id}>
                <tr className={e.detail ? "ui-table__main-row" : undefined}>
                  <td>{formatWhen(e.observed_at)}</td>
                  <td>{e.source}</td>
                  <td>{e.dialed ?? "—"}</td>
                  <td>
                    {e.extension ? `${e.extension}@${e.domain ?? "?"}` : "—"}
                  </td>
                  <td>{e.node ?? "—"}</td>
                  <td>
                    {e.source === "webhook" ? (
                      <Badge tone="degraded">at dial time</Badge>
                    ) : e.egress_ok === null ? (
                      <Badge tone="degraded">unattributed</Badge>
                    ) : (
                      <Badge tone={e.egress_ok ? "online" : "offline"}>
                        {e.egress_ok ? "home node" : "OFF HOME NODE"}
                      </Badge>
                    )}
                  </td>
                  <td>
                    {e.connected === null ? (
                      "—"
                    ) : (
                      <Badge tone={e.connected ? "online" : "offline"}>
                        {e.connected
                          ? `yes${e.duration_seconds !== null ? ` · ${e.duration_seconds} s` : ""}`
                          : "NOT ANSWERED"}
                      </Badge>
                    )}
                  </td>
                </tr>
                {e.detail ? (
                  <tr className="ui-table__detail-row">
                    <td colSpan={7}>
                      <span className="ui-table__detail-label">Detail</span>
                      {e.detail}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

export function E911Page(): ReactNode {
  const devices = useQuery({
    queryKey: ["e911", "devices"],
    queryFn: getE911Devices,
  });
  const [selected, setSelected] = useState<number | null>(null);
  const items = devices.data?.items ?? [];
  const selectedRow = items.find((d) => d.device_id === selected) ?? null;

  return (
    <div>
      <div className="dash__head">
        <h1>E911</h1>
      </div>
      <Panel title={`Devices (${items.length})`}>
        {devices.isLoading ? (
          <Spinner />
        ) : items.length === 0 ? (
          <div className="ui-empty">No devices in the fleet yet.</div>
        ) : (
          <table className="ui-table">
            <thead>
              <tr>
                <th>MAC</th>
                <th>Label</th>
                <th>Location</th>
                <th>Home node</th>
                <th>Emergency ANI</th>
                <th>State</th>
                <th>Blockers</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.device_id}>
                  <td>{d.mac}</td>
                  <td>{d.label ?? "—"}</td>
                  <td>{d.location_name ?? "—"}</td>
                  <td>{d.home_node_name ?? "—"}</td>
                  <td>{d.did_number ?? "—"}</td>
                  <td>
                    <Badge
                      tone={d.e911_state === "verified" ? "online" : "degraded"}
                    >
                      {d.e911_state}
                    </Badge>
                  </td>
                  <td>{d.blockers.length === 0 ? "—" : d.blockers.length}</td>
                  <td>
                    <Button
                      variant="secondary"
                      onClick={() => setSelected(d.device_id)}
                    >
                      Manage…
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
      {selectedRow ? (
        <DeviceDetail key={selectedRow.device_id} status={selectedRow} />
      ) : null}
      <SyncPanel />
      <EventsPanel />
    </div>
  );
}
